import React, { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";
import Cargando from "../components/Cargando";
import { Ayuda } from "../context/AyudaContext";
import CampoForm from "../components/CampoForm";

const formaVacia = {
  nombre: "",
  correo: "",
  rol: "solicitante",
  area_id: "",
  cargo: "",
  edificio_id: "",
  aprueba_todo: true,
  rangos: [],
};

const ETIQUETAS_ROL = {
  solicitante: { texto: "Solicitante", color: "#5b6472", bg: "#eef1f3" },
  aprobador: { texto: "Aprobador", color: "var(--tema-primario, #2f6690)", bg: "var(--tema-suave, #dceaf5)" },
  compras: { texto: "Compras", color: "#3d5a99", bg: "#e1e7f5" },
  administrador: { texto: "Administrador", color: "#fff", bg: "var(--tema-titulo, #0b3c60)" },
  pagos: { texto: "Pagos", color: "#0f7c8a", bg: "#d5eef1" },
};

// Resumen de lo que puede aprobar un aprobador (montos en córdobas).
function textoRango(u) {
  if (u.rol !== "aprobador") return "—";
  if (u.aprueba_todo !== false) return "General (todo)";
  const f = (n) => `C$${Number(n).toLocaleString("es-NI")}`;
  const partes = (u.rangos || []).map((r) => {
    const min = r.monto_min;
    const max = r.monto_max;
    if (min !== null && max !== null) return `${f(min)} a ${f(max)}`;
    if (min !== null) return `Desde ${f(min)}`;
    return `Hasta ${f(max)}`;
  });
  return partes.length ? partes.join(" · ") : "Sin rangos";
}

const PALETA_AVATAR = ["#3d5a99", "var(--tema-primario, #2f6690)", "#2f7a54", "#b8722a", "#a3402f", "#5b6472"];

function iniciales(nombre) {
  if (!nombre) return "?";
  const partes = nombre.trim().split(/\s+/);
  return ((partes[0]?.[0] || "") + (partes[1]?.[0] || "")).toUpperCase();
}

function colorAvatar(nombre) {
  if (!nombre) return PALETA_AVATAR[0];
  let hash = 0;
  for (let i = 0; i < nombre.length; i++) hash = nombre.charCodeAt(i) + ((hash << 5) - hash);
  return PALETA_AVATAR[Math.abs(hash) % PALETA_AVATAR.length];
}

function GestionarUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [areas, setAreas] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [form, setForm] = useState(formaVacia);
  const [editandoId, setEditandoId] = useState(null);
  const [formAbierto, setFormAbierto] = useState(false);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(true);

  const [busqueda, setBusqueda] = useState("");
  const [filtroArea, setFiltroArea] = useState("");
  // Pestaña: por defecto solo los activos; los inactivos se ven en su propia pestaña.
  const [verInactivos, setVerInactivos] = useState(false);

  const cargar = () => {
    setCargando(true);
    Promise.all([
      apiFetch("/api/usuarios"),
      apiFetch("/api/areas"),
      apiFetch("/api/edificios"),
    ])
      .then(([usuariosData, areasData, edificiosData]) => {
        setUsuarios(usuariosData || []);
        setAreas(areasData || []);
        setEdificios(edificiosData || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, []);

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nombre.trim() || (!editandoId && !form.correo.trim())) {
      setError("Nombre y correo son obligatorios.");
      return;
    }
    try {
      if (editandoId) {
        await apiFetch(`/api/usuarios/${editandoId}`, {
          method: "POST",
          body: JSON.stringify({
            nombre: form.nombre,
            rol: form.rol,
            area_id: form.area_id || null,
            cargo: form.cargo || null,
            edificio_id: form.edificio_id || null,
            aprueba_todo: form.aprueba_todo,
            rangos: form.rangos,
          }),
        });
      } else {
        await apiFetch("/api/usuarios", { method: "POST", body: JSON.stringify(form) });
      }
      setForm(formaVacia);
      setEditandoId(null);
      setFormAbierto(false);
      cargar();
    } catch (err) {
      setError(err.message);
    }
  };

  const editar = (u) => {
    setEditandoId(u.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setForm({
      nombre: u.nombre,
      correo: u.correo,
      rol: u.rol,
      area_id: u.area_id || "",
      cargo: u.cargo || "",
      edificio_id: u.edificio_id || "",
      aprueba_todo: u.aprueba_todo !== false,
      rangos: (u.rangos || []).map((r) => ({
        monto_min: r.monto_min ?? "",
        monto_max: r.monto_max ?? "",
      })),
    });
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setFormAbierto(false);
    setForm(formaVacia);
  };

  const cambiarActivo = async (u) => {
    try {
      await apiFetch(`/api/usuarios/${u.id}/activo`, {
        method: "POST",
        body: JSON.stringify({ activo: u.activo ? 0 : 1 }),
      });
      cargar();
    } catch (err) {
      setError(err.message);
    }
  };

  const termino = busqueda.trim().toLowerCase();
  const usuariosFiltrados = usuarios.filter((u) => {
    const coincideBusqueda =
      !termino ||
      u.nombre?.toLowerCase().includes(termino) ||
      u.correo?.toLowerCase().includes(termino);
    const coincideArea = !filtroArea || String(u.area_id) === String(filtroArea);
    const coincideEstado = verInactivos ? !u.activo : !!u.activo;
    return coincideBusqueda && coincideArea && coincideEstado;
  });
  const totalActivos = usuarios.filter((u) => !!u.activo).length;
  const totalInactivos = usuarios.length - totalActivos;
  const hayFiltrosActivos = !!busqueda || !!filtroArea;

  return (
    <div>
      <h2 style={{ margin: 0, color: "var(--tema-titulo, #0b3c60)", textAlign: "center" }}>👥 Gestionar Usuarios</h2>
      <p style={{ color: "#5b6472", marginTop: "0.25rem", textAlign: "center" }}>
        {usuarios.length} usuario{usuarios.length === 1 ? "" : "s"} registrado{usuarios.length === 1 ? "" : "s"}
        {" "}en el sistema.
      </p>

      {!formAbierto && !editandoId && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.75rem" }}>
          <button type="button" onClick={() => setFormAbierto(true)} style={styles.botonAgregar}>
            + Agregar usuario
          </button>
        </div>
      )}

      {(formAbierto || editandoId) && (
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.cardIcono}>{editandoId ? "✏️" : "➕"}</span>
          <h3 style={{ margin: 0, fontSize: "1rem", color: "var(--tema-titulo, #0b3c60)" }}>
            {editandoId ? "Editando usuario" : "Agregar usuario"}
          </h3>
        </div>

        <form onSubmit={guardar}>
          <section className="form-seccion">
            <div className="form-seccion-titulo">👤 Datos de la persona</div>
            <div className="form-grid" style={styles.grid}>
              <CampoForm etiqueta="Nombre completo" obligatorio>
                <input
                  value={form.nombre}
                  onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                  style={styles.input}
                />
              </CampoForm>
              <CampoForm etiqueta="Correo institucional" obligatorio>
                <input
                  placeholder="correo@dsm.edu.ni"
                  value={form.correo}
                  disabled={!!editandoId}
                  onChange={(e) => setForm((f) => ({ ...f, correo: e.target.value }))}
                  style={styles.input}
                />
              </CampoForm>
              <CampoForm etiqueta="Cargo">
                <input
                  value={form.cargo}
                  onChange={(e) => setForm((f) => ({ ...f, cargo: e.target.value }))}
                  style={styles.input}
                />
              </CampoForm>
            </div>
          </section>

          <section className="form-seccion">
            <div className="form-seccion-titulo">🏫 Rol y ubicación</div>
            <div className="form-grid" style={styles.grid}>
              <CampoForm etiqueta="Rol en el sistema">
                <select
                  value={form.rol}
                  onChange={(e) => setForm((f) => ({ ...f, rol: e.target.value }))}
                  style={styles.input}
                >
                  <option value="solicitante">Solicitante</option>
                  <option value="aprobador">Aprobador</option>
                  <option value="compras">Compras</option>
                  <option value="pagos">Pagos</option>
                  <option value="administrador">Administrador</option>
                </select>
              </CampoForm>
              <CampoForm etiqueta="Área">
                <select
                  value={form.area_id}
                  onChange={(e) => setForm((f) => ({ ...f, area_id: e.target.value }))}
                  style={styles.input}
                >
                  <option value="">Elegir...</option>
                  {areas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nombre}
                    </option>
                  ))}
                </select>
              </CampoForm>
              <CampoForm etiqueta="Edificio">
                <select
                  value={form.edificio_id}
                  onChange={(e) => setForm((f) => ({ ...f, edificio_id: e.target.value }))}
                  style={styles.input}
                >
                  <option value="">Elegir...</option>
                  {edificios.map((ed) => (
                    <option key={ed.id} value={ed.id}>
                      {ed.nombre}
                    </option>
                  ))}
                </select>
              </CampoForm>
            </div>
          </section>
          {form.rol === "aprobador" && (
            <div style={styles.bloqueMontos}>
              <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--tema-titulo, #0b3c60)", marginBottom: "0.5rem" }}>
                💼 Qué cotizaciones puede aprobar
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                <input
                  type="checkbox"
                  checked={form.aprueba_todo}
                  onChange={(e) => setForm((f) => ({ ...f, aprueba_todo: e.target.checked }))}
                />
                <span>
                  <strong>Aprobador general</strong> — aprueba cotizaciones de cualquier monto
                </span>
              </label>

              {!form.aprueba_todo && (
                <>
                  <Ayuda>
                  <div style={{ fontSize: "0.78rem", color: "#5b6472", marginBottom: "0.4rem" }}>
                    Montos totales de la cotización en córdobas (las de dólares se convierten con su tipo de
                    cambio). Puedes agregar varios rangos.
                  </div>
                  </Ayuda>
                  {form.rangos.map((r, i) => (
                    <div key={i} style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center", marginBottom: "0.4rem" }}>
                      <label style={styles.campoMonto}>
                        Desde C$
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Sin mínimo"
                          value={r.monto_min}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              rangos: f.rangos.map((x, k) => (k === i ? { ...x, monto_min: e.target.value } : x)),
                            }))
                          }
                          style={{ ...styles.input, maxWidth: 140 }}
                        />
                      </label>
                      <label style={styles.campoMonto}>
                        Hasta C$
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Sin máximo"
                          value={r.monto_max}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              rangos: f.rangos.map((x, k) => (k === i ? { ...x, monto_max: e.target.value } : x)),
                            }))
                          }
                          style={{ ...styles.input, maxWidth: 140 }}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, rangos: f.rangos.filter((_, k) => k !== i) }))}
                        style={styles.botonFilaPeligro}
                        title="Quitar este rango"
                      >
                        🗑️ Quitar
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({ ...f, rangos: [...f.rangos, { monto_min: "", monto_max: "" }] }))
                    }
                    style={styles.botonFila}
                  >
                    + Agregar rango
                  </button>
                  <Ayuda>
                  <div style={{ fontSize: "0.75rem", color: "#8993a1", marginTop: "0.4rem" }}>
                    Ej.: Desde 2000 sin máximo = cotizaciones mayores de C$2,000. Desde 2000 hasta 10000 = solo
                    ese tramo. El administrador siempre puede aprobar todo.
                  </div>
                  </Ayuda>
                </>
              )}
            </div>
          )}
          {editandoId && (
            <Ayuda>
            <p style={{ fontSize: "0.8rem", color: "#5b6472", margin: "0 0 0.75rem" }}>
              El correo no se puede cambiar aquí — es la cuenta con la que esa persona inicia sesión.
            </p>
            </Ayuda>
          )}
          <button type="submit" style={styles.botonAgregar}>
            {editandoId ? "Guardar cambios" : "Guardar usuario"}
          </button>{" "}
          <button type="button" onClick={cancelarEdicion} style={styles.botonCancelar}>
            Cancelar
          </button>
        </form>
      </div>
      )}

      {error && <p style={{ color: "#a3402f", marginTop: "0.75rem" }}>{error}</p>}

      <div style={styles.pestanas} role="tablist" aria-label="Estado de los usuarios">
        <button
          type="button"
          role="tab"
          aria-selected={!verInactivos}
          onClick={() => setVerInactivos(false)}
          style={{ ...styles.pestana, ...(!verInactivos ? styles.pestanaActiva : {}) }}
        >
          ✅ Activos <span style={styles.pestanaCuenta}>{totalActivos}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={verInactivos}
          onClick={() => setVerInactivos(true)}
          style={{ ...styles.pestana, ...(verInactivos ? styles.pestanaActiva : {}) }}
        >
          ⏸️ Inactivos <span style={styles.pestanaCuenta}>{totalInactivos}</span>
        </button>
      </div>

      <div style={styles.barraBusqueda}>
        <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
          <span style={styles.iconoBusqueda}>🔍</span>
          <input
            type="text"
            placeholder="Buscar por nombre o correo..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ ...styles.input, width: "100%", boxSizing: "border-box", paddingLeft: "1.9rem" }}
          />
        </div>
        <select
          value={filtroArea}
          onChange={(e) => setFiltroArea(e.target.value)}
          style={{ ...styles.input, minWidth: 180 }}
        >
          <option value="">Todas las áreas</option>
          {areas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nombre}
            </option>
          ))}
        </select>
        {hayFiltrosActivos && (
          <button
            type="button"
            onClick={() => {
              setBusqueda("");
              setFiltroArea("");
            }}
            style={styles.botonCancelar}
          >
            🧹 Limpiar
          </button>
        )}
      </div>

      {cargando ? (
        <Cargando compacto />
      ) : usuariosFiltrados.length === 0 ? (
        <p style={{ marginTop: "0.75rem", color: "#5b6472" }}>
          📭 {hayFiltrosActivos
            ? "Ningún usuario coincide con esa búsqueda."
            : verInactivos
            ? "No hay usuarios inactivos."
            : "No hay usuarios activos."}
        </p>
      ) : (
        <div className="table-scroll" style={styles.tablaWrap}>
          <table style={styles.tabla}>
            <thead>
              <tr style={{ textAlign: "center", borderBottom: "1px solid #d3d9df" }}>
                <th style={styles.th}>Nombre</th>
                <th style={styles.th}>Correo</th>
                <th style={styles.th}>Rol</th>
                <th style={styles.th}>Área</th>
                <th style={styles.th}>Cargo</th>
                <th style={styles.th}>Aprobación</th>
                <th style={styles.th}>Edificio</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {usuariosFiltrados.map((u, i) => {
                const rolInfo = ETIQUETAS_ROL[u.rol] || { texto: u.rol, color: "#5b6472", bg: "#eef1f3" };
                return (
                  <tr
                    key={u.id}
                    style={{
                      borderBottom: "1px solid #eef1f3",
                      background: editandoId === u.id ? "#f2e3d1" : i % 2 === 1 ? "#fafbfc" : "transparent",
                      opacity: u.activo ? 1 : 0.6,
                    }}
                  >
                    <td style={styles.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <span style={{ ...styles.avatar, background: colorAvatar(u.nombre) }}>
                          {iniciales(u.nombre)}
                        </span>
                        {u.nombre}
                      </div>
                    </td>
                    <td style={styles.td}>{u.correo}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, color: rolInfo.color, background: rolInfo.bg }}>
                        {rolInfo.texto}
                      </span>
                    </td>
                    <td style={styles.td}>{u.area_nombre || "—"}</td>
                    <td style={styles.td}>{u.cargo || "—"}</td>
                    <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{textoRango(u)}</td>
                    <td style={styles.td}>{u.edificio_nombre || "—"}</td>
                    <td style={styles.td}>
                      {u.activo ? (
                        <span style={{ ...styles.badge, color: "#2f7a54", background: "#dcf0e6" }}>● Activo</span>
                      ) : (
                        <span style={{ ...styles.badge, color: "#a3402f", background: "#f7ded9" }}>● Inactivo</span>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      <button onClick={() => editar(u)} style={styles.botonFila}>
                        ✏️ Editar
                      </button>{" "}
                      <button
                        onClick={() => cambiarActivo(u)}
                        style={u.activo ? styles.botonFilaPeligro : styles.botonFilaExito}
                      >
                        {u.activo ? "Inactivar" : "Activar"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  pestanas: { display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.9rem" },
  pestana: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.45rem",
    padding: "0.45rem 0.95rem",
    border: "1px solid var(--tema-linea, #d5deec)",
    borderRadius: 999,
    background: "#fff",
    color: "#3d4a5c",
    fontWeight: 700,
    fontSize: "0.85rem",
    fontFamily: "inherit",
    cursor: "pointer",
  },
  pestanaActiva: {
    background: "var(--tema-primario, #1f4e8c)",
    borderColor: "var(--tema-primario, #1f4e8c)",
    color: "#fff",
  },
  pestanaCuenta: {
    fontSize: "0.72rem",
    background: "rgba(255,255,255,0.25)",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 999,
    padding: "0 0.45rem",
  },
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1.1rem 1.2rem",
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.6rem",
    marginBottom: "1rem",
  },
  cardIcono: {
    width: 34,
    height: 34,
    borderRadius: 9,
    background: "var(--tema-suave, #dceaf5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.1rem",
    flexShrink: 0,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "0.6rem",
  },
  input: { padding: "0.45rem 0.6rem", border: "1px solid #d3d9df", borderRadius: 6, fontFamily: "inherit", fontSize: "0.88rem" },
  bloqueMontos: {
    background: "#f7f8fa",
    border: "1px solid #eef1f3",
    borderRadius: 8,
    padding: "0.7rem 0.85rem",
    marginBottom: "0.75rem",
  },
  campoMonto: { display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.82rem", fontWeight: 600 },
  barraBusqueda: {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
    alignItems: "center",
    margin: "0.9rem 0",
  },
  iconoBusqueda: {
    position: "absolute",
    left: "0.55rem",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "0.8rem",
    pointerEvents: "none",
  },
  botonAgregar: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.5rem 1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  botonCancelar: {
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.5rem 1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  tablaWrap: { border: "1px solid #eef1f3", borderRadius: 10 },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" },
  th: {
    padding: "0.55rem 0.5rem",
    background: "#eef1f3",
    border: "1px solid #d3d9df",
    fontSize: "0.76rem",
    color: "var(--tema-titulo, #0b3c60)",
  },
  td: { padding: "0.5rem 0.6rem", textAlign: "left" },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.68rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  badge: {
    display: "inline-block",
    padding: "0.18rem 0.6rem",
    borderRadius: 999,
    fontSize: "0.75rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  botonFila: {
    border: "1px solid #d3d9df",
    background: "#fff",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "var(--tema-titulo, #0b3c60)",
    cursor: "pointer",
  },
  botonFilaPeligro: {
    border: "1px solid #f7ded9",
    background: "#fff",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#a3402f",
    cursor: "pointer",
  },
  botonFilaExito: {
    border: "1px solid #cdeadb",
    background: "#fff",
    borderRadius: 6,
    padding: "0.3rem 0.6rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#2f7a54",
    cursor: "pointer",
  },
};

export default GestionarUsuarios;
